import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertCircle, Eye, EyeOff, Loader2, ShieldCheck, CalendarCheck, Users } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth, homePathForRole } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const SIDE_IMAGE =
  "https://images.unsplash.com/photo-1496660879542-a78ca4fc8b0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxjYWxtJTIwbm90ZWJvb2slMjBkZXNrJTIwbWluaW1hbHxlbnwwfHx8fDE3ODc4NjI1NDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [identifiant, setIdentifiant] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!identifiant.trim() || !password) {
      setError("Veuillez renseigner votre identifiant et votre mot de passe.");
      return;
    }
    setLoading(true);
    try {
      const user = await login(identifiant.trim(), password);
      navigate(homePathForRole(user.role), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full h-12 px-4 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 outline-none transition-[background-color,border-color,box-shadow] duration-200 focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-600/10 dark:bg-white/5 dark:border-white/10 dark:text-slate-100";

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Panneau de marque (desktop) */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img src={SIDE_IMAGE} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-blue-950/80" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Logo size={44} />
            <span className="font-heading text-lg font-semibold tracking-tight">Le Passeport</span>
          </div>
          <div className="max-w-md">
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight">
              Le suivi hebdomadaire des élèves, en toute sérénité.
            </h2>
            <p className="mt-4 text-blue-200">
              Un espace clair et bienveillant pour accompagner chaque élève et informer les familles, semaine après semaine.
            </p>
            <div className="mt-8 space-y-3">
              {[
                { icon: CalendarCheck, text: "Un passeport clair chaque semaine" },
                { icon: Users, text: "Les familles informées et impliquées" },
                { icon: ShieldCheck, text: "Vos données protégées et confidentielles" },
              ].map((f) => (
                <div key={f.text} className="flex items-center gap-3 text-sm text-blue-100">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10"><f.icon size={16} /></span>
                  {f.text}
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-blue-300/70">© {new Date().getFullYear()} Le Passeport</p>
        </div>
      </div>

      {/* Panneau formulaire */}
      <div className="flex w-full items-center justify-center p-6 sm:p-10 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-[400px]" data-testid="login-card"
        >
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <Logo size={52} />
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Bon retour 👋</h1>
          <p className="mt-2 mb-8 text-base text-slate-500 dark:text-slate-400">Connectez-vous à votre espace.</p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
            <div className="flex flex-col gap-2">
              <label htmlFor="identifiant" className="text-sm font-medium text-slate-700 dark:text-slate-300">Identifiant ou e-mail</label>
              <input id="identifiant" type="text" autoComplete="username" value={identifiant}
                onChange={(e) => setIdentifiant(e.target.value)} className={inputClass}
                placeholder="ex. prof.martin ou vous@ecole.fr" data-testid="login-identifiant-input" />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-300">Mot de passe</label>
              <div className="relative">
                <input id="password" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className={`${inputClass} pr-12`} placeholder="••••••••" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} data-testid="login-toggle-password">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-sm font-medium text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400" data-testid="login-error" role="alert">
                <AlertCircle size={18} className="shrink-0" /><span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={loading}
              className="mt-1 flex h-12 w-full items-center justify-center rounded-full bg-blue-900 font-medium text-white shadow-lg shadow-blue-900/20 transition-transform duration-200 hover:scale-[0.99] active:scale-95 disabled:opacity-60 disabled:hover:scale-100 dark:bg-blue-500"
              data-testid="login-submit-button">
              {loading ? <Loader2 size={20} className="animate-spin" /> : "Se connecter"}
            </button>

            <div className="text-center">
              <button type="button" onClick={() => navigate("/mot-de-passe-oublie")}
                className="text-sm text-slate-500 transition-colors hover:text-blue-800 dark:hover:text-blue-300" data-testid="login-forgot-password">
                Mot de passe oublié ?
              </button>
            </div>
          </form>

          <div className="mt-8 border-t border-slate-100 pt-6 text-center dark:border-white/10">
            <p className="text-sm text-slate-500 dark:text-slate-400">Vous êtes enseignant ?</p>
            <button type="button" onClick={() => navigate("/inscription")}
              className="mt-2 text-sm font-semibold text-blue-900 underline-offset-4 hover:underline dark:text-blue-300" data-testid="go-register-button">
              Créer un compte enseignant
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
